import type { TourDefinition } from "@tiny/tour";

export const NAVIGATE_AFTER: Record<number, string> = {
  0: "/main/dashboard/overview", // retake safety: always land on dashboard for step 1
  1: "/main/automations/overview",
  6: "/main/campaigns/overview",
  8: "/main/campaigns/calendar",
  11: "/main/forms",
  13: "/main/workflows",
};

// Steps that trigger a programmatic DOM action after the user advances.
// Handled in MasterTour.tsx via onStepAfter.
// 4 → open automation preview drawer
// 5 → close preview drawer before showing activate toggle

export const masterOnboardingTour: TourDefinition = {
  id: "master-onboarding-v1",
  promptTitle: "Welcome to tinyAlbert!",
  promptDescription:
    "Let us show you how tinyAlbert has already set everything up for you — in about 5 minutes.",
  steps: [
    // ── Dashboard ──────────────────────────────────────────────────────────
    {
      target: "body",
      title: "Welcome to tinyAlbert 👋",
      content:
        "Email marketing used to mean hours of writing, designing, and scheduling. tinyAlbert takes all of that off your plate — it learns your brand, generates your content, and runs your campaigns automatically. Let's show you what's already been built for you.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "body",
      title: "Everything is ready. You just need to activate.",
      content:
        "Before you've clicked a single button, tinyAlbert got to work. It's written your automation sequences, generated over 1,000 campaign templates, and built your signup forms — all with your brand voice, your colours, and your tone. This tour walks you through each one.",
      placement: "center",
      skipBeacon: true,
    },
    // ── Automations ────────────────────────────────────────────────────────
    {
      target: "body",
      title: "Your automations are already written.",
      content:
        "Automations are email sequences that trigger automatically — when someone subscribes, abandons a cart, makes a purchase, or goes quiet. tinyAlbert has already written 6 of them using your brand language, preferred tone, and colours. They're here and ready.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "#automation-cards-section",
      title: "6 sequences, built for your brand.",
      content:
        "Each card is a fully written email sequence — the trigger, the timing, and all the copy. These were generated using your brand setup: your language, your tone, and your store's style. You don't need to write a word.",
      placement: "top",
      skipBeacon: true,
    },
    {
      target: "#automations-overview-list-preview",
      title: "Let's open a preview.",
      content:
        "Click Next and we'll open the preview so you can see exactly what tinyAlbert wrote — the subject line, the body copy, and the full email layout.",
      placement: "top",
      skipBeacon: true,
    },
    {
      target: "body",
      title: "This is what tinyAlbert wrote for you.",
      content:
        "Every word here was generated using your brand voice, tone, and language preferences. The subject line, the body copy, the call-to-action — all of it. If you want to change anything, you can edit directly from this panel before activating.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "#automations-overview-list-toggle-activate",
      title: "One toggle to go live.",
      content:
        "Happy with it? Flip this toggle and the automation starts immediately — sending to eligible customers 24/7, without any further action from you.",
      placement: "top",
      skipBeacon: true,
    },
    // ── Campaigns overview ─────────────────────────────────────────────────
    {
      target: "body",
      title: "1,000+ campaigns — for your store, for the whole year.",
      content:
        "tinyAlbert has generated over 1,000 campaign templates tailored to your business. Each one is built with your brand images, your colour palette, your products from your store, and written in your tone — covering every major event, season, and promotion for the next full year.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "#campaign-cards-section",
      title: "Browse and preview your campaigns.",
      content:
        "These are your generated campaigns. Click any card to preview the full email — subject line, design, copy, and product blocks — all personalised for your brand. When you're ready, you can schedule and activate it directly from the preview.",
      placement: "top",
      skipBeacon: true,
    },
    // ── Campaign Calendar ──────────────────────────────────────────────────
    {
      target: "body",
      title: "Your full year is already mapped out.",
      content:
        "The Campaign Calendar gives you a bird's-eye view of your entire send schedule. All your generated campaigns are mapped to the dates and events that matter most for your industry — so you always have something relevant ready to go.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "[data-tour='calendar-info-alert']",
      title: "Built specifically for your industry.",
      content:
        "This calendar was built based on your store category and the retail moments that drive sales in your space. Every campaign shown here has a fully generated email behind it — subject line, copy, layout, and product blocks. Click any to preview it.",
      placement: "bottom",
      skipBeacon: true,
    },
    {
      target: "#add-new-campaign",
      title: "Add your own any time.",
      content:
        "Running a flash sale or a one-off promotion outside the calendar? Hit 'Add New', pick a date, describe what you're sending — tinyAlbert writes the email. It slots straight into the calendar alongside your pre-planned campaigns.",
      placement: "bottom",
      skipBeacon: true,
    },
    // ── Forms ──────────────────────────────────────────────────────────────
    {
      target: "body",
      title: "Grow your subscriber list automatically.",
      content:
        "As your campaigns drive traffic to your site, forms capture those visitors as subscribers. More subscribers means more people receive your campaigns and automations — compounding your results over time. tinyAlbert has already written four form types for you.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "#forms-overview-list-toggle-activate",
      title: "Enable it and it goes live instantly.",
      content:
        "Flip this toggle and the form goes live on your website automatically. No embed code, no developer needed. It starts capturing subscribers immediately, feeding directly into your automations and campaigns.",
      placement: "top",
      skipBeacon: true,
    },
    // ── Workflows ──────────────────────────────────────────────────────────
    {
      target: "body",
      title: "Build any customer journey you can imagine.",
      content:
        "Workflows go beyond the pre-built automations. Loyalty sequences, conditional paths based on purchase history, complex onboarding flows — you connect triggers, delays, conditions, and email actions on a visual canvas. No code required.",
      placement: "center",
      skipBeacon: true,
    },
    {
      target: "#workflows-add-new",
      title: "Start from a template or build from scratch.",
      content:
        "Click 'Add New' to get started. Choose a predefined template — proven journeys you can customise and launch in minutes — or open a blank canvas and design every step yourself. Once you activate it, it runs automatically.",
      placement: "bottom",
      skipBeacon: true,
    },
    // ── Finish ─────────────────────────────────────────────────────────────
    {
      target: "body",
      title: "You're ready. Here's where to start.",
      content:
        "tinyAlbert has done the heavy lifting. A good first session: activate 2–3 automations, enable a form, and explore your campaign calendar. That alone puts your email marketing on autopilot — and tinyAlbert keeps improving as it learns more about your customers.",
      placement: "center",
      skipBeacon: true,
    },
  ],
};
